import { z } from 'zod';

/**
 * Bump when the Yjs document layout changes in a backwards-incompatible way.
 * Migrations are forward-only and run at app start, keyed off this number.
 */
export const SCHEMA_VERSION = 11;

const Id = z.string().min(1);
const Timestamp = z.number().int().nonnegative();
const NonEmptyString = z.string().min(1);

export const PRIORITIES = [0, 1, 2, 3] as const;
export const Priority = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
export type PriorityT = z.infer<typeof Priority>;

export const Energy = z.enum(['low', 'med', 'high']);
export type EnergyT = z.infer<typeof Energy>;

export const Daypart = z.enum(['morning', 'afternoon', 'evening', 'night']);
export type DaypartT = z.infer<typeof Daypart>;

export const TaskWorkflowState = NonEmptyString;
export type TaskWorkflowStateT = z.infer<typeof TaskWorkflowState>;
export const DEFAULT_TASK_WORKFLOW_STATE: TaskWorkflowStateT = 'backlog';

export const DEFAULT_WORKFLOW_COLUMNS = [
  { id: 'backlog', name: 'Backlog', order: 0, protected: true },
  { id: 'next', name: 'Next', order: 1, protected: false },
  { id: 'in_progress', name: 'In Progress', order: 2, protected: false },
  { id: 'waiting', name: 'Waiting', order: 3, protected: false },
] as const;

export const ObjectiveStatus = z.enum(['on_track', 'at_risk', 'off_track']);
export type ObjectiveStatusT = z.infer<typeof ObjectiveStatus>;

export const KeyResultProgressMode = z.enum(['manual', 'numeric', 'task_rollup']);
export type KeyResultProgressModeT = z.infer<typeof KeyResultProgressMode>;

export const CheckInCadence = z.enum(['daily', 'weekly']);
export type CheckInCadenceT = z.infer<typeof CheckInCadence>;

export const CheckInTargetType = z.enum(['objective', 'key_result']);
export type CheckInTargetTypeT = z.infer<typeof CheckInTargetType>;

export const CommitmentState = z.enum([
  'active_now',
  'waiting',
  'later_parked',
  'someday',
  'needs_clarification',
]);
export type CommitmentStateT = z.infer<typeof CommitmentState>;

export const Importance = z.enum(['standard', 'important', 'protected']);
export type ImportanceT = z.infer<typeof Importance>;

export const InitiativeLifecycle = z.enum(['planned', 'active', 'done', 'cancelled']);
export type InitiativeLifecycleT = z.infer<typeof InitiativeLifecycle>;

export const TaskActionability = z.enum([
  'concrete',
  'needs_decomposition',
  'placeholder',
  'not_applicable',
]);
export type TaskActionabilityT = z.infer<typeof TaskActionability>;

export const PrioritizationEntityType = z.enum(['task', 'objective', 'initiative']);
export type PrioritizationEntityTypeT = z.infer<typeof PrioritizationEntityType>;

export const ValueState = z.enum(['known', 'explicit_unknown', 'not_applicable']);
export type ValueStateT = z.infer<typeof ValueState>;

export const EvidenceSource = z.enum([
  'imported',
  'system_derived',
  'llm_proposed',
  'user_confirmed',
]);
export type EvidenceSourceT = z.infer<typeof EvidenceSource>;

export const RecurrenceSchema = z.object({
  /** Stable id shared by task instances in the same repeating series. */
  seriesId: Id.optional(),
  /** RFC 5545 RRULE string (without DTSTART). */
  rrule: NonEmptyString,
  /** When true, next occurrence is computed from completion date, not previous due. */
  fromCompletion: z.boolean(),
});
export type Recurrence = z.infer<typeof RecurrenceSchema>;

export const AreaSchema = z.object({
  id: Id,
  name: NonEmptyString,
  color: z.string().optional(),
  order: z.number(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Area = z.infer<typeof AreaSchema>;

export const ProjectSchema = z.object({
  id: Id,
  name: NonEmptyString,
  areaId: Id.optional(),
  /** True for the special always-present Inbox project. */
  inbox: z.boolean().optional(),
  archived: z.boolean(),
  order: z.number(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Project = z.infer<typeof ProjectSchema>;

export const ObjectiveSchema = z.object({
  id: Id,
  projectId: Id,
  parentObjectiveId: Id.optional(),
  title: NonEmptyString,
  description: z.string().optional(),
  status: ObjectiveStatus,
  startDate: Timestamp.optional(),
  endDate: Timestamp.optional(),
  outcome: z.string().min(1).optional(),
  commitmentState: CommitmentState.optional(),
  importance: Importance.optional(),
  reviewAt: Timestamp.optional(),
  minimumProgressCadenceDays: z.number().int().positive().optional(),
  order: z.number(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Objective = z.infer<typeof ObjectiveSchema>;

export const InitiativeSchema = z.object({
  id: Id,
  projectId: Id,
  title: NonEmptyString,
  description: z.string().optional(),
  successDefinition: NonEmptyString.optional(),
  lifecycle: InitiativeLifecycle,
  commitmentState: CommitmentState.optional(),
  importance: Importance.optional(),
  reviewAt: Timestamp.optional(),
  reviewTrigger: NonEmptyString.optional(),
  minimumProgressCadenceDays: z.number().int().positive().optional(),
  objectiveIds: z.array(Id),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Initiative = z.infer<typeof InitiativeSchema>;

const BaseKeyResultSchema = z.object({
  id: Id,
  objectiveId: Id,
  title: NonEmptyString,
  description: z.string().optional(),
  progressMode: KeyResultProgressMode,
  manualProgress: z.number().min(0).max(100).optional(),
  startValue: z.number().optional(),
  currentValue: z.number().optional(),
  targetValue: z.number().optional(),
  status: ObjectiveStatus.optional(),
  confidence: z.number().min(0).max(100).optional(),
  order: z.number(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});

export const KeyResultSchema = BaseKeyResultSchema.superRefine((value, ctx) => {
  if (value.progressMode === 'manual' && value.manualProgress === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'manualProgress is required when progressMode is manual',
      path: ['manualProgress'],
    });
  }

  if (
    value.progressMode === 'numeric' &&
    (value.currentValue === undefined || value.targetValue === undefined)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'currentValue and targetValue are required when progressMode is numeric',
      path: ['currentValue'],
    });
  }
});
export type KeyResult = z.infer<typeof KeyResultSchema>;

export const CheckInSchema = z.object({
  id: Id,
  cadence: CheckInCadence,
  targetType: CheckInTargetType,
  targetId: Id,
  note: NonEmptyString,
  blockers: z.string().optional(),
  nextSteps: z.string().optional(),
  statusUpdate: ObjectiveStatus.optional(),
  confidenceUpdate: z.number().min(0).max(100).optional(),
  progressSnapshot: z.number().min(0).max(100).optional(),
  createdAt: Timestamp,
});
export type CheckIn = z.infer<typeof CheckInSchema>;

export const ReminderPreferencesSchema = z.object({
  dueNudgesEnabled: z.boolean(),
  dueLeadMinutes: z.number().int().nonnegative(),
  scheduledNudgesEnabled: z.boolean(),
  scheduledLeadMinutes: z.number().int().nonnegative(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type ReminderPreferences = z.infer<typeof ReminderPreferencesSchema>;

export const DEFAULT_REMINDER_PREFERENCES = {
  dueNudgesEnabled: true,
  dueLeadMinutes: 60,
  scheduledNudgesEnabled: true,
  scheduledLeadMinutes: 15,
} as const;

export const PriorityColorMapSchema = z.object({
  0: z.string(),
  1: z.string(),
  2: z.string(),
  3: z.string(),
});
export type PriorityColorMap = z.infer<typeof PriorityColorMapSchema>;
export const TagColorMapSchema = z.record(z.string(), z.string());
export type TagColorMap = z.infer<typeof TagColorMapSchema>;

export const DEFAULT_PRIORITY_COLORS: PriorityColorMap = {
  0: '#9ca3af',
  1: '#dc2626',
  2: '#a5f3fc',
  3: '#14b8a6',
};

export const TaskSwipeActionSchema = z.enum(['complete', 'postpone']);
export type TaskSwipeActionT = z.infer<typeof TaskSwipeActionSchema>;

export const DEFAULT_TASK_SWIPE_ACTIONS = {
  leftToRight: 'complete',
  rightToLeft: 'postpone',
} as const satisfies Record<'leftToRight' | 'rightToLeft', TaskSwipeActionT>;

export const FavoriteShortcutSchema = z.object({
  kind: z.enum(['project', 'smart']),
  id: Id,
});
export type FavoriteShortcut = z.infer<typeof FavoriteShortcutSchema>;

export const ProjectSidebarSortSchema = z.enum(['alphabetical', 'manual']);
export type ProjectSidebarSortT = z.infer<typeof ProjectSidebarSortSchema>;

export const ProjectSidebarCountSchema = z.enum(['active', 'total']);
export type ProjectSidebarCountT = z.infer<typeof ProjectSidebarCountSchema>;

export const AppBadgeCountModeSchema = z.enum(['today', 'open']);
export type AppBadgeCountModeT = z.infer<typeof AppBadgeCountModeSchema>;

export const UiPreferencesSchema = z.object({
  priorityColors: PriorityColorMapSchema,
  tagColors: TagColorMapSchema.default({}),
  taskSwipeLeftToRight: TaskSwipeActionSchema.default(DEFAULT_TASK_SWIPE_ACTIONS.leftToRight),
  taskSwipeRightToLeft: TaskSwipeActionSchema.default(DEFAULT_TASK_SWIPE_ACTIONS.rightToLeft),
  favoriteShortcuts: z.array(FavoriteShortcutSchema).default([]),
  projectSidebarSort: ProjectSidebarSortSchema.default('alphabetical'),
  projectSidebarCount: ProjectSidebarCountSchema.default('active'),
  projectSidebarShowArchived: z.boolean().default(false),
  appBadgeCountMode: AppBadgeCountModeSchema.default('today'),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type UiPreferences = z.infer<typeof UiPreferencesSchema>;

export const SavedViewGroupBy = z.enum([
  'none',
  'project',
  'tag',
  'priority',
  'due',
  'scheduled',
  'workflow',
  'estimate',
  'owner',
]);
export type SavedViewGroupByT = z.infer<typeof SavedViewGroupBy>;

export const SavedViewSortKey = z.enum([
  'manual',
  'name',
  'priority',
  'due',
  'scheduled',
  'tag',
  'project',
  'estimate',
  'created',
  'updated',
]);
export type SavedViewSortKeyT = z.infer<typeof SavedViewSortKey>;

export const SortDirection = z.enum(['asc', 'desc']);
export type SortDirectionT = z.infer<typeof SortDirection>;

export const SubtaskDisplay = z.enum(['flat', 'nested']);
export type SubtaskDisplayT = z.infer<typeof SubtaskDisplay>;

export const SavedViewSortSchema = z.object({
  key: SavedViewSortKey,
  direction: SortDirection,
});
export type SavedViewSort = z.infer<typeof SavedViewSortSchema>;

export const TaskArrangementSchema = z.object({
  id: Id,
  name: NonEmptyString,
  groupBy: SavedViewGroupBy.default('project'),
  groupDirection: SortDirection.default('asc'),
  sort: z.array(SavedViewSortSchema).default([{ key: 'due', direction: 'asc' }]),
  order: z.number(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type TaskArrangement = z.infer<typeof TaskArrangementSchema>;

export const SelectedTaskArrangementSchema = z.object({
  listKey: NonEmptyString,
  arrangementId: NonEmptyString,
  subtaskDisplay: SubtaskDisplay.default('flat'),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type SelectedTaskArrangement = z.infer<typeof SelectedTaskArrangementSchema>;

export const SavedViewRuleField = z.enum([
  'text',
  'title',
  'completed',
  'trashed',
  'priority',
  'due',
  'scheduled',
  'tag',
  'project',
  'workflow',
  'estimate',
  'timeestimate',
  'recurrence',
  'owner',
  'url',
  'hasurl',
  'hasnotes',
  'location',
  'postponed',
  'source',
  'favorite',
  'energy',
  'daypart',
  'child',
  'issubtask',
  'root',
  'haschildren',
  'hassubtasks',
  'raw',
]);
export type SavedViewRuleFieldT = z.infer<typeof SavedViewRuleField>;

export const SavedViewConditionOperator = z.enum([
  'is',
  'is_not',
  'contains',
  'exists',
  'missing',
  'before',
  'after',
  'on',
  'less_than',
  'greater_than',
]);
export type SavedViewConditionOperatorT = z.infer<typeof SavedViewConditionOperator>;

export type SavedViewRule =
  | {
      type: 'group';
      operator: 'all' | 'any' | 'none';
      children: SavedViewRule[];
    }
  | {
      type: 'condition';
      field: SavedViewRuleFieldT;
      operator: SavedViewConditionOperatorT;
      value?: string | undefined;
    };

export const SavedViewRuleSchema: z.ZodType<SavedViewRule> = z.lazy(() =>
  z.union([
    z.object({
      type: z.literal('group'),
      operator: z.enum(['all', 'any', 'none']),
      children: z.array(SavedViewRuleSchema),
    }),
    z.object({
      type: z.literal('condition'),
      field: SavedViewRuleField,
      operator: SavedViewConditionOperator,
      value: z.string().optional(),
    }),
  ]),
);

export const TaskSchema = z.object({
  id: Id,
  title: NonEmptyString,
  notes: z.string().optional(),
  projectId: Id,
  parentTaskId: Id.optional(),
  krId: Id.optional(),
  objectiveId: Id.optional(),
  initiativeId: Id.optional(),
  planningContextIds: z.array(Id).optional(),
  workflowState: TaskWorkflowState.default(DEFAULT_TASK_WORKFLOW_STATE),
  commitmentState: CommitmentState.optional(),
  actionability: TaskActionability.optional(),
  expectedOutcome: NonEmptyString.optional(),
  availabilityTrigger: NonEmptyString.optional(),
  waitingReason: NonEmptyString.optional(),
  reviewAt: Timestamp.optional(),
  priority: Priority,
  dueAt: Timestamp.optional(),
  dueAtHasTime: z.boolean().optional(),
  scheduledAt: Timestamp.optional(),
  scheduledAtHasTime: z.boolean().optional(),
  reminderAt: Timestamp.optional(),
  estimateMinutes: z.number().int().positive().optional(),
  energy: Energy.optional(),
  daypart: Daypart.optional(),
  location: z.string().optional(),
  owner: z.string().optional(),
  url: z.string().url().or(z.string().min(1)).optional(),
  postponed: z.number().int().nonnegative().optional(),
  source: z.string().optional(),
  favorite: z.boolean().optional(),
  tags: z.array(z.string()),
  dependsOn: z.array(Id).optional(),
  recurrence: RecurrenceSchema.optional(),
  manualOrder: z.number().optional(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
  completedAt: Timestamp.optional(),
  trashedAt: Timestamp.optional(),
});
export type Task = z.infer<typeof TaskSchema>;

export const PlanningRunTrigger = z.enum(['manual', 'daily', 'weekly', 'mcp_prompt']);
export const PlanningRunState = z.enum([
  'queued',
  'running',
  'awaiting_user',
  'awaiting_llm',
  'completed',
  'cancelled',
  'failed',
]);
export const PlanningStage = z.enum([
  'prioritization',
  'refinement',
  'execution_planning',
  'scheduling',
  'execution',
  'review',
]);
export const ExecutorType = z.enum(['human', 'ai', 'hybrid']);
export const PlanningContextScope = z.enum(['work', 'home', 'global']);
export const PlanningRecordStatus = z.enum(['proposed', 'approved', 'rejected', 'completed']);
export const RiskLevel = z.enum(['low', 'medium', 'high', 'destructive']);

export const PlanningRunSchema = z.object({
  id: Id,
  trigger: PlanningRunTrigger,
  horizonStart: Timestamp,
  horizonEnd: Timestamp,
  state: PlanningRunState,
  currentStage: PlanningStage,
  inputFingerprint: NonEmptyString,
  requestedContext: PlanningContextScope.optional(),
  leaseOwner: z.string().optional(),
  leaseExpiresAt: Timestamp.optional(),
  error: z.string().optional(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
  completedAt: Timestamp.optional(),
});
export type PlanningRun = z.infer<typeof PlanningRunSchema>;

export const PlanStepSchema = z.object({
  id: Id,
  planningRunId: Id,
  taskId: Id.optional(),
  parentStepId: Id.optional(),
  title: NonEmptyString,
  outcome: NonEmptyString,
  acceptanceCriteria: z.array(NonEmptyString),
  dependencyIds: z.array(Id),
  estimateMinutes: z.number().int().positive(),
  executor: ExecutorType,
  contextIds: z.array(Id),
  status: PlanningRecordStatus,
  order: z.number().int().nonnegative(),
  /**
   * Deduplication key for retryable submissions. Either a caller-supplied key
   * or a content fingerprint derived by the API worker, so a transport failure
   * after a committed write cannot produce a duplicate step (REQ-API-002).
   *
   * Additive and optional on purpose: no SCHEMA_VERSION bump, so client builds
   * that predate this field keep working instead of hitting the downgrade
   * guard in runMigrations.
   */
  idempotencyKey: NonEmptyString.optional(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type PlanStep = z.infer<typeof PlanStepSchema>;

export const ScheduleBlockState = z.enum([
  'proposed',
  'approved',
  'booked',
  'rejected',
  'drifted',
  'cancelled',
]);
export const ScheduleAlternativeSchema = z.object({
  startAt: Timestamp,
  endAt: Timestamp,
  explanation: NonEmptyString,
});
export const ScheduleBlockSchema = z
  .object({
    id: Id,
    planningRunId: Id,
    taskId: Id.optional(),
    planStepId: Id.optional(),
    startAt: Timestamp,
    endAt: Timestamp,
    timezone: NonEmptyString,
    state: ScheduleBlockState,
    explanation: NonEmptyString,
    alternatives: z.array(ScheduleAlternativeSchema).max(2),
    googleCalendarId: z.string().optional(),
    googleEventId: z.string().optional(),
    googleEventEtag: z.string().optional(),
    lastReconciledAt: Timestamp.optional(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
  })
  .superRefine((value, ctx) => {
    if ((value.taskId === undefined) === (value.planStepId === undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'exactly one work target is required' });
    }
    if (value.endAt <= value.startAt) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'endAt must be after startAt' });
    }
  });
export type ScheduleBlock = z.infer<typeof ScheduleBlockSchema>;

export const ApprovalState = z.enum(['pending', 'approved', 'rejected', 'expired', 'consumed']);
export const ApprovalRequestSchema = z.object({
  id: Id,
  planningRunId: Id.optional(),
  action: NonEmptyString,
  connector: NonEmptyString,
  accountId: z.string().optional(),
  operation: NonEmptyString,
  diff: z.array(
    z.object({
      path: NonEmptyString,
      before: z.unknown().optional(),
      after: z.unknown().optional(),
    }),
  ),
  risk: RiskLevel,
  state: ApprovalState,
  requiredConfirmation: NonEmptyString,
  inputFingerprint: NonEmptyString,
  expiresAt: Timestamp,
  decidedBy: z.string().optional(),
  decidedAt: Timestamp.optional(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type ApprovalRequest = z.infer<typeof ApprovalRequestSchema>;

export const AutomationPolicySchema = z.object({
  id: Id,
  name: NonEmptyString,
  enabled: z.boolean(),
  connector: NonEmptyString,
  accountId: z.string().optional(),
  operations: z.array(NonEmptyString).min(1),
  maximumRisk: z.enum(['low', 'medium', 'high']),
  contextIds: z.array(Id),
  schedule: z
    .object({
      cadence: z.enum(['daily', 'weekly']),
      localHour: z.number().int().min(0).max(23),
      timezone: NonEmptyString,
      weekday: z.number().int().min(0).max(6).optional(),
    })
    .optional(),
  expiresAt: Timestamp.optional(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type AutomationPolicy = z.infer<typeof AutomationPolicySchema>;

export const ExecutionState = z.enum(['queued', 'running', 'succeeded', 'failed', 'cancelled']);
export const ExecutionRunSchema = z
  .object({
    id: Id,
    planningRunId: Id,
    planStepId: Id.optional(),
    taskId: Id.optional(),
    scheduleBlockId: Id.optional(),
    approvalRequestId: Id.optional(),
    connector: NonEmptyString,
    operation: NonEmptyString,
    inputFingerprint: NonEmptyString,
    idempotencyKey: NonEmptyString,
    state: ExecutionState,
    attemptCount: z.number().int().nonnegative(),
    leaseOwner: z.string().optional(),
    leaseExpiresAt: Timestamp.optional(),
    error: z.string().optional(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
    completedAt: Timestamp.optional(),
  })
  .superRefine((value, ctx) => {
    if ([value.planStepId, value.taskId, value.scheduleBlockId].every((id) => id === undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'an execution target is required' });
    }
  });
export type ExecutionRun = z.infer<typeof ExecutionRunSchema>;

export const ExecutionReceiptSchema = z.object({
  id: Id,
  executionRunId: Id,
  actor: NonEmptyString,
  tool: NonEmptyString,
  outputReference: z.string().optional(),
  outputSummary: z.string().optional(),
  outputFingerprint: NonEmptyString,
  createdAt: Timestamp,
});
export type ExecutionReceipt = z.infer<typeof ExecutionReceiptSchema>;

export const ContextFactState = z.enum(['proposed', 'confirmed', 'expired', 'rejected']);
export const ContextFactSchema = z.object({
  id: Id,
  scope: PlanningContextScope,
  key: NonEmptyString,
  value: z.string(),
  state: ContextFactState,
  sensitivity: z.enum(['normal', 'sensitive']),
  sourceType: z.enum(['task', 'calendar', 'email', 'notes', 'user']),
  sourceReference: z.string().optional(),
  observedAt: Timestamp,
  freshUntil: Timestamp,
  confirmedBy: z.string().optional(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type ContextFact = z.infer<typeof ContextFactSchema>;

export const ReviewPeriod = z.enum(['daily', 'weekly']);
export const ReviewReportSchema = z.object({
  id: Id,
  planningRunId: Id.optional(),
  period: ReviewPeriod,
  periodStart: Timestamp,
  periodEnd: Timestamp,
  completedTaskIds: z.array(Id),
  slippedTaskIds: z.array(Id),
  changedPriorityTaskIds: z.array(Id),
  observations: z.array(NonEmptyString),
  recommendations: z.array(
    z.object({ id: Id, summary: NonEmptyString, approvalRequestId: Id.optional() }),
  ),
  narrative: z.string().optional(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type ReviewReport = z.infer<typeof ReviewReportSchema>;

export const DailyCapacitySchema = z.object({
  id: Id,
  localDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  timezone: NonEmptyString,
  availableMinutes: z.number().int().nonnegative(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type DailyCapacity = z.infer<typeof DailyCapacitySchema>;

export const ProtectedBlockSchema = z
  .object({
    id: Id,
    localDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    timezone: NonEmptyString,
    durationMinutes: z.number().int().positive(),
    startAt: Timestamp.optional(),
    endAt: Timestamp.optional(),
    objectiveId: Id.optional(),
    initiativeId: Id.optional(),
    note: z.string().optional(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
  })
  .superRefine((value, ctx) => {
    if ((value.objectiveId === undefined) === (value.initiativeId === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'exactly one of objectiveId or initiativeId is required',
        path: ['objectiveId'],
      });
    }
    if (value.startAt !== undefined && value.endAt !== undefined && value.endAt <= value.startAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'endAt must be after startAt',
        path: ['endAt'],
      });
    }
  });
export type ProtectedBlock = z.infer<typeof ProtectedBlockSchema>;

export const FieldEvidenceSchema = z.object({
  id: Id,
  entityType: PrioritizationEntityType,
  entityId: Id,
  field: NonEmptyString,
  valueState: ValueState,
  source: EvidenceSource,
  actor: NonEmptyString,
  tool: NonEmptyString.optional(),
  valueHash: NonEmptyString.optional(),
  confirmationId: NonEmptyString.optional(),
  proposalFingerprint: NonEmptyString.optional(),
  recordedAt: Timestamp,
});
export type FieldEvidence = z.infer<typeof FieldEvidenceSchema>;

export const ProgressEventSchema = z.object({
  id: Id,
  entityType: z.enum(['objective', 'initiative']),
  entityId: Id,
  occurredAt: Timestamp,
  note: z.string().optional(),
  source: EvidenceSource,
  actor: NonEmptyString,
  tool: NonEmptyString.optional(),
  createdAt: Timestamp,
});
export type ProgressEvent = z.infer<typeof ProgressEventSchema>;

export const EnrichmentChangeSchema = z.object({
  field: NonEmptyString,
  before: z.unknown().optional(),
  after: z.unknown().optional(),
  valueState: ValueState.optional(),
});
export type EnrichmentChange = z.infer<typeof EnrichmentChangeSchema>;

export const EnrichmentAction = z.enum([
  'proposed',
  'confirmed',
  'rejected',
  'skipped',
  'deferred',
  'parked',
  'left_unknown',
  'trashed',
]);
export type EnrichmentActionT = z.infer<typeof EnrichmentAction>;

export const EnrichmentEventSchema = z.object({
  id: Id,
  entityType: PrioritizationEntityType,
  entityId: Id,
  action: EnrichmentAction,
  changes: z.array(EnrichmentChangeSchema),
  actor: NonEmptyString,
  tool: NonEmptyString.optional(),
  confirmationId: NonEmptyString.optional(),
  proposalFingerprint: NonEmptyString.optional(),
  createdAt: Timestamp,
});
export type EnrichmentEvent = z.infer<typeof EnrichmentEventSchema>;

export const TriageDispositionSchema = z.object({
  id: Id,
  entityType: PrioritizationEntityType,
  entityId: Id,
  disposition: z.enum(['skipped', 'rejected', 'deferred']),
  reasonCodes: z.array(NonEmptyString),
  sourceFingerprint: NonEmptyString,
  resurfaceAt: Timestamp.optional(),
  actor: NonEmptyString,
  tool: NonEmptyString.optional(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type TriageDisposition = z.infer<typeof TriageDispositionSchema>;

export const WorkflowColumnSchema = z.object({
  id: Id,
  name: NonEmptyString,
  order: z.number(),
  protected: z.boolean().optional(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type WorkflowColumn = z.infer<typeof WorkflowColumnSchema>;

export const SmartListSchema = z.object({
  id: Id,
  name: NonEmptyString,
  query: z.string(),
  ruleTree: SavedViewRuleSchema.optional(),
  groupBy: SavedViewGroupBy.default('project'),
  groupDirection: SortDirection.default('asc'),
  sort: z.array(SavedViewSortSchema).default([{ key: 'due', direction: 'asc' }]),
  showCompleted: z.boolean().default(true),
  showTrashed: z.boolean().default(false),
  order: z.number(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type SmartList = z.infer<typeof SmartListSchema>;

export const TaskActivityType = z.enum([
  'comment',
  'created',
  'completed',
  'reopened',
  'workflow_changed',
  'project_changed',
  'parent_changed',
  'priority_changed',
  'due_changed',
  'scheduled_changed',
]);
export type TaskActivityTypeT = z.infer<typeof TaskActivityType>;

export const TaskActivitySchema = z.object({
  id: Id,
  taskId: Id,
  type: TaskActivityType,
  message: NonEmptyString,
  createdAt: Timestamp,
});
export type TaskActivity = z.infer<typeof TaskActivitySchema>;

/**
 * JSON export/import envelope. This is the shape written to / read from a
 * downloaded `.json` file. Import is validated against this schema before
 * any data is written into the Yjs document.
 */
export const ExportDataSchema = z.object({
  schemaVersion: z.number().int().positive(),
  exportedAt: z.string().datetime(),
  projects: z.array(ProjectSchema),
  initiatives: z.array(InitiativeSchema).optional(),
  reminderPreferences: ReminderPreferencesSchema.optional(),
  uiPreferences: UiPreferencesSchema.optional(),
  objectives: z.array(ObjectiveSchema).optional(),
  keyResults: z.array(KeyResultSchema).optional(),
  checkIns: z.array(CheckInSchema).optional(),
  tasks: z.array(TaskSchema),
  workflowColumns: z.array(WorkflowColumnSchema).optional(),
  smartLists: z.array(SmartListSchema).optional(),
  taskArrangements: z.array(TaskArrangementSchema).optional(),
  selectedTaskArrangements: z.array(SelectedTaskArrangementSchema).optional(),
  taskActivities: z.array(TaskActivitySchema).optional(),
  dailyCapacities: z.array(DailyCapacitySchema).optional(),
  protectedBlocks: z.array(ProtectedBlockSchema).optional(),
  fieldEvidence: z.array(FieldEvidenceSchema).optional(),
  progressEvents: z.array(ProgressEventSchema).optional(),
  enrichmentEvents: z.array(EnrichmentEventSchema).optional(),
  triageDispositions: z.array(TriageDispositionSchema).optional(),
  planningRuns: z.array(PlanningRunSchema).optional(),
  planSteps: z.array(PlanStepSchema).optional(),
  scheduleBlocks: z.array(ScheduleBlockSchema).optional(),
  approvalRequests: z.array(ApprovalRequestSchema).optional(),
  automationPolicies: z.array(AutomationPolicySchema).optional(),
  executionRuns: z.array(ExecutionRunSchema).optional(),
  executionReceipts: z.array(ExecutionReceiptSchema).optional(),
  contextFacts: z.array(ContextFactSchema).optional(),
  reviewReports: z.array(ReviewReportSchema).optional(),
});
export type ExportData = z.infer<typeof ExportDataSchema>;
